import React, { useCallback, useEffect, useState } from 'react'
import { Text, Button, Heading } from '../ui'
import { InformativoVersion, NewsLikeSummary } from '../../types/informativo'
import { getNewsLikesSummary } from '../../services/informativo'
import { DashboardCardSkeleton } from './DashboardSkeleton'
import NewsLikeButton from './NewsLikeButton'

interface CirculandoHomeSectionProps {
  versions: InformativoVersion[]
  loading: boolean
  error: string | null
  onVersionClick: (file: string | null, link: string | null, title?: string) => void
}

const formatPublishedDate = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

const formatMonthYear = (date: string) => {
  const raw = new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

const isMonthYearTitle = (title: string, date: string) => {
  const normalizedTitle = title.trim().toLocaleLowerCase('pt-BR')
  const monthYear = formatMonthYear(date).toLocaleLowerCase('pt-BR')
  return normalizedTitle === monthYear
}

const emptyLike = (): NewsLikeSummary => ({ count: 0, likedByMe: false })

const patternBg =
  "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"

const CirculandoHomeSection: React.FC<CirculandoHomeSectionProps> = ({
  versions,
  loading,
  error,
  onVersionClick,
}) => {
  const latestVersion = versions[0]
  const newsId = latestVersion?.id || ''
  const isPdf = Boolean(latestVersion?.file)
  const actionLabel = isPdf ? 'Abrir PDF' : 'Abrir link'
  const [likeSummary, setLikeSummary] = useState<NewsLikeSummary>(emptyLike)

  useEffect(() => {
    let cancelled = false
    if (!newsId) {
      setLikeSummary(emptyLike())
      return
    }

    getNewsLikesSummary([newsId])
      .then((map) => {
        if (!cancelled) setLikeSummary(map[newsId] || emptyLike())
      })
      .catch(() => {
        if (!cancelled) setLikeSummary(emptyLike())
      })

    return () => {
      cancelled = true
    }
  }, [newsId])

  const handleLikeChanged = useCallback((_id: string, next: NewsLikeSummary) => {
    setLikeSummary(next)
  }, [])

  const bannerTitle = latestVersion?.title
    ? `Circulando: ${
        isMonthYearTitle(latestVersion.title, latestVersion.date)
          ? formatMonthYear(latestVersion.date)
          : latestVersion.title
      }`
    : null

  if (!loading && !error && versions.length === 0) return null

  return (
    <section className="h-full py-0">
      {loading && <DashboardCardSkeleton className="min-h-[16rem] rounded-2xl" />}

      {error && !loading && (
        <div className="rounded-xl border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-4">
          <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
            {error}
          </Text>
        </div>
      )}

      {!loading && !error && latestVersion && (
        <article className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md h-full flex flex-col">
          <div
            className="relative flex items-center overflow-hidden px-4 py-3 sm:px-5 sm:py-3.5"
            style={{
              background:
                'linear-gradient(135deg, #115e59 0%, #008B95 60%, #2dd4bf 100%)',
            }}
          >
            <div
              className="pointer-events-none absolute inset-0"
              style={{ backgroundImage: patternBg }}
              aria-hidden
            />
            <div className="relative flex w-full min-w-0 items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                {bannerTitle && (
                  <Heading
                    as="h3"
                    size="xl"
                    className="!text-white text-[1.15rem] sm:text-xl font-extrabold leading-snug tracking-tight drop-shadow-sm line-clamp-3"
                  >
                    {bannerTitle}
                  </Heading>
                )}
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                {newsId ? (
                  <NewsLikeButton
                    newsId={newsId}
                    summary={likeSummary}
                    onChanged={handleLikeChanged}
                  />
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onVersionClick(latestVersion.file, latestVersion.link, latestVersion.title)
                  }
                  className="flex-shrink-0 bg-white ring-white/40 hover:bg-gray-100 focus:ring-white/25 !px-3.5 !py-1.5 text-xs font-semibold"
                >
                  {actionLabel}
                </Button>
              </div>
            </div>
          </div>

          <div className="px-5 py-4 sm:px-6">
            {latestVersion.description ? (
              <Text
                size="sm"
                className="text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-4 mb-3"
              >
                {latestVersion.description}
              </Text>
            ) : null}
            <Text size="xs" className="text-gray-400 dark:text-gray-500">
              Publicado em {formatPublishedDate(latestVersion.date)}
            </Text>
          </div>
        </article>
      )}
    </section>
  )
}

export default CirculandoHomeSection
